import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, subDays } from "date-fns";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useBrandContext } from "@/contexts/brand-context";
import {
  useInboxVolume,
  useInboxHeatmap,
  useInboxResponseTime,
  useInboxTopAccounts,
} from "@/hooks/use-inbox-analytics";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale, formatNumber } from "@/i18n/format";

type ReportRange = 7 | 30 | 90 | 365;

const HOURS = Array.from({ length: 24 }, (_, i) => i);
// dow from the API: 1 = Monday .. 7 = Sunday
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 7];

function humanizeSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

function cellOpacity(received: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(0, Math.min(1, received / max));
}

function RangeSelector({
  value,
  onChange,
}: {
  value: ReportRange;
  onChange: (v: ReportRange) => void;
}) {
  return (
    <ToggleGroup
      type="single"
      value={String(value)}
      onValueChange={(v) => {
        if (!v) return;
        const n = Number(v);
        if (n === 7 || n === 30 || n === 90 || n === 365) onChange(n);
      }}
      variant="outline"
      size="sm"
    >
      <ToggleGroupItem value="7">7d</ToggleGroupItem>
      <ToggleGroupItem value="30">30d</ToggleGroupItem>
      <ToggleGroupItem value="90">90d</ToggleGroupItem>
      <ToggleGroupItem value="365">365d</ToggleGroupItem>
    </ToggleGroup>
  );
}

function SectionSkeleton({ className }: { className?: string }) {
  return <Skeleton className={className ?? "h-64 w-full rounded-lg"} />;
}

function ConfigureEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function ErrorSection({ message }: { message: string }) {
  return (
    <p className="py-8 text-center text-sm text-destructive">{message}</p>
  );
}

export function InboxReportsPage() {
  const { t } = useTranslation("inbox");
  const { brand } = useBrandContext();
  const locale = useLocale();
  const [range, setRange] = useState<ReportRange>(30);

  const { fromDate, toDate } = useMemo(() => {
    const to = new Date();
    const from = subDays(to, range);
    return {
      fromDate: format(from, "yyyy-MM-dd"),
      toDate: format(to, "yyyy-MM-dd"),
    };
  }, [range]);

  const volume = useInboxVolume(brand.id, fromDate, toDate);
  const heatmap = useInboxHeatmap(brand.id, fromDate, toDate);
  const responseTime = useInboxResponseTime(brand.id, fromDate, toDate);
  const topAccounts = useInboxTopAccounts(brand.id, fromDate, toDate);

  const notConfigured =
    volume.data === null ||
    heatmap.data === null ||
    responseTime.data === null ||
    topAccounts.data === null;

  const heatmapMax = useMemo(() => {
    if (!heatmap.data) return 0;
    return heatmap.data.buckets.reduce((max, b) => Math.max(max, b.received), 0);
  }, [heatmap.data]);

  const heatmapByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of heatmap.data?.buckets ?? []) {
      map.set(`${b.dow}:${b.hour}`, b.received);
    }
    return map;
  }, [heatmap.data]);

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("reports.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("reports.subtitle")}</p>
        </div>
        <RangeSelector value={range} onChange={setRange} />
      </div>

      {notConfigured ? (
        <ConfigureEmptyState message={t("reports.notConfigured")} />
      ) : (
        <>
          {/* KPI row */}
          {volume.isLoading ? (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <SectionSkeleton key={i} className="h-24 rounded-lg" />
              ))}
            </div>
          ) : volume.isError ? (
            <ErrorSection message={t("reports.error")} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("reports.kpi.received")}
                </div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(volume.data?.summary.received ?? 0, locale)}
                </div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("reports.kpi.sent")}
                </div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(volume.data?.summary.sent ?? 0, locale)}
                </div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("reports.kpi.read")}
                </div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(volume.data?.summary.read ?? 0, locale)}
                </div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("reports.kpi.conversations")}
                </div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(volume.data?.summary.uniqueConversations ?? 0, locale)}
                </div>
              </div>
            </div>
          )}

          {/* Volume chart + platform split */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg border bg-card p-4">
              <h3 className="mb-3 text-sm font-medium">{t("reports.volume")}</h3>
              {volume.isLoading ? (
                <SectionSkeleton className="h-64 w-full rounded-lg" />
              ) : volume.isError ? (
                <ErrorSection message={t("reports.error")} />
              ) : (volume.data?.timeseries.length ?? 0) === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t("reports.empty")}
                </p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={volume.data?.timeseries ?? []}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="received"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="sent"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="rounded-lg border bg-card p-4">
              <h3 className="mb-3 text-sm font-medium">{t("reports.byPlatform")}</h3>
              {volume.isLoading ? (
                <SectionSkeleton className="h-64 w-full rounded-lg" />
              ) : volume.isError ? (
                <ErrorSection message={t("reports.error")} />
              ) : (volume.data?.byPlatform.length ?? 0) === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t("reports.empty")}
                </p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={volume.data?.byPlatform ?? []}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="received" fill="hsl(var(--primary))" />
                      <Bar dataKey="sent" fill="hsl(var(--muted-foreground))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Heatmap */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-medium">{t("reports.heatmap")}</h3>
            <p className="mb-3 text-xs text-muted-foreground">{t("reports.heatmapHint")}</p>
            {heatmap.isLoading ? (
              <SectionSkeleton className="h-56 w-full rounded-lg" />
            ) : heatmap.isError ? (
              <ErrorSection message={t("reports.error")} />
            ) : (
              <div className="overflow-x-auto">
                <div className="inline-block">
                  <div
                    className="grid gap-0.5"
                    style={{ gridTemplateColumns: `36px repeat(24, minmax(14px, 1fr))` }}
                  >
                    <div />
                    {HOURS.map((h) => (
                      <div key={h} className="text-center text-[9px] text-muted-foreground">
                        {h % 3 === 0 ? h : ""}
                      </div>
                    ))}
                  </div>
                  {DOW_ORDER.map((dow) => (
                    <div
                      key={dow}
                      className="mt-0.5 grid gap-0.5"
                      style={{ gridTemplateColumns: `36px repeat(24, minmax(14px, 1fr))` }}
                    >
                      <div className="flex items-center text-[10px] text-muted-foreground">
                        {t(`reports.dow.${dow}`)}
                      </div>
                      {HOURS.map((h) => {
                        const received = heatmapByKey.get(`${dow}:${h}`) ?? 0;
                        const opacity = cellOpacity(received, heatmapMax);
                        return (
                          <div
                            key={h}
                            className="h-4 rounded-sm bg-primary"
                            title={`${t(`reports.dow.${dow}`)} ${h}:00 — ${received}`}
                            style={{ opacity: opacity === 0 ? 0.08 : opacity }}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Response time */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="mb-3 text-sm font-medium">{t("reports.responseTime")}</h3>
            {responseTime.isLoading ? (
              <SectionSkeleton className="h-40 w-full rounded-lg" />
            ) : responseTime.isError ? (
              <ErrorSection message={t("reports.error")} />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t("reports.median")}
                    </div>
                    <div className="mt-2 text-2xl font-semibold">
                      {humanizeSeconds(responseTime.data?.summary.medianSeconds ?? 0)}
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t("reports.p90")}
                    </div>
                    <div className="mt-2 text-2xl font-semibold">
                      {humanizeSeconds(responseTime.data?.summary.p90Seconds ?? 0)}
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t("reports.p99")}
                    </div>
                    <div className="mt-2 text-2xl font-semibold">
                      {humanizeSeconds(responseTime.data?.summary.p99Seconds ?? 0)}
                    </div>
                  </div>
                </div>
                {(responseTime.data?.histogram.length ?? 0) > 0 && (
                  <div className="mt-4 h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={responseTime.data?.histogram ?? []}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="hsl(var(--primary))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Top accounts */}
          <div className="rounded-lg border bg-card">
            <div className="border-b p-3">
              <h3 className="text-sm font-medium">{t("reports.topAccounts")}</h3>
            </div>
            {topAccounts.isLoading ? (
              <div className="p-4">
                <SectionSkeleton className="h-32 w-full rounded-lg" />
              </div>
            ) : topAccounts.isError ? (
              <div className="p-4">
                <ErrorSection message={t("reports.error")} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">{t("reports.account")}</th>
                      <th className="px-3 py-2 text-right">{t("reports.medianResponse")}</th>
                      <th className="px-3 py-2 text-right">{t("reports.replies")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topAccounts.data?.accounts.length ?? 0) === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-8 text-center text-sm text-muted-foreground"
                        >
                          {t("reports.empty")}
                        </td>
                      </tr>
                    ) : (
                      topAccounts.data?.accounts.map((a) => (
                        <tr key={a.accountId} className="border-t">
                          <td className="px-3 py-2">{a.displayName}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {humanizeSeconds(a.medianResponseSeconds)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatNumber(a.repliedCount, locale)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
